import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { mdiFolder, mdiFileDocument } from '@mdi/js';
import { DropResult } from '@hello-pangea/dnd';

import {
    DragDropContext,
    Draggable,
    Droppable,
} from 'common/components/DragDrop';
import { LibraryListItem } from 'library/components/LibraryListItem';
import { List } from 'common/components/List';
import {
    ISubCollectionFragment,
    SubCollectionFragmentDoc,
    ICollectionArticleFragment,
    CollectionArticleFragmentDoc,
    UpdateCollectionsDocument,
    UpdateArticlesDocument,
} from 'graphql/types';
import { reorder } from 'common/utils/draggable';

type IListItem =
    | Partial<ISubCollectionFragment>
    | Partial<ICollectionArticleFragment>;

interface IProps {
    collectionId?: string;
    droppableType?: 'COLLECTION' | 'ARTICLE';
    canSort?: boolean;
    items: IListItem[];
}

export const LibraryItemsList = ({
    items,
    collectionId,
    droppableType,
    canSort,
}: IProps) => {
    const [listItems, setListItems] = useState<IListItem[]>([]);

    useEffect(() => {
        setListItems(items);
    }, [items]);

    const [updateCollectionsOrder] = useMutation(UpdateCollectionsDocument, {
        update: (cache, { data }) => {
            const updatedCollections =
                data?.updateLibraryCollections?.collections;

            if (!updatedCollections) return;

            cache.modify({
                id: `LibraryCollection:${collectionId}`,
                fields: {
                    collections() {
                        // To make sure we update the order and references correctly we map over the
                        // updatedCollections and use writeFragment to return the ref.
                        return updatedCollections.map((collection) =>
                            cache.writeFragment({
                                id: cache.identify(collection),
                                fragment: SubCollectionFragmentDoc,
                                data: collection,
                            })
                        );
                    },
                },
            });
        },
    });
    const [updateArticlesOrder] = useMutation(UpdateArticlesDocument, {
        update: (cache, { data }) => {
            const updatedArticles = data?.updateLibraryArticles?.articles;

            if (!updatedArticles) return;

            cache.modify({
                id: `LibraryCollection:${collectionId}`,
                fields: {
                    articles() {
                        // To make sure we update the order and references correctly we map over the
                        // updatedArticles and use writeFragment to return the ref.
                        return updatedArticles.map((article) =>
                            cache.writeFragment({
                                id: cache.identify(article),
                                fragment: CollectionArticleFragmentDoc,
                                data: article,
                            })
                        );
                    },
                },
            });
        },
    });

    const handleDragEnd = ({ type, destination, source }: DropResult) => {
        if (!destination) return;

        const orderedItems = reorder(
            listItems,
            source.index,
            destination.index
        );

        setListItems(orderedItems);

        if (type === 'COLLECTION') {
            updateCollectionsOrder({
                variables: {
                    orderIds: orderedItems
                        .map((item) => item.id)
                        .filter(Boolean),
                },
            });

            return;
        }

        updateArticlesOrder({
            variables: {
                orderIds: orderedItems.map((item) => item.id).filter(Boolean),
            },
        });
    };

    const renderListItem = (listItem: IListItem) => {
        const { id, title, description, thumbnail, headerImage } = listItem;

        const imageUrl = thumbnail?.url || headerImage?.url;

        if (listItem.__typename === 'LibraryCollection') {
            return (
                <LibraryListItem
                    color="secondary"
                    description={description}
                    hidden={!listItem.active}
                    icon={mdiFolder}
                    imageUrl={imageUrl}
                    params={{ id }}
                    title={title}
                    to="LIBRARY_COLLECTION"
                />
            );
        }

        return (
            <LibraryListItem
                showFullSecondary
                color="primary"
                description={description}
                icon={mdiFileDocument}
                imageUrl={imageUrl}
                params={{ id }}
                title={title}
                to="LIBRARY_ARTICLE"
            />
        );
    };

    const itemNodes = listItems.map((listItem, index) => {
        if (!listItem.id) return null;

        return (
            <Draggable
                dragDropDisabled={!canSort}
                draggableId={listItem.id}
                index={index}
                key={listItem.id}
            >
                {renderListItem(listItem)}
            </Draggable>
        );
    });

    return (
        <List>
            <DragDropContext
                dragDropDisabled={!canSort}
                onDragEnd={handleDragEnd}
            >
                <Droppable
                    dragDropDisabled={!canSort}
                    droppableId={droppableType}
                    type={droppableType}
                >
                    {itemNodes}
                </Droppable>
            </DragDropContext>
        </List>
    );
};
